import type { LocalizedText } from '~/types/site'

export type BusinessType = 'solo' | 'clinic'

export type ModuleKey =
  | 'blog'
  | 'gallery'
  | 'testimonials'
  | 'faq'
  | 'shop'
  | 'insurance'
  | 'team'
  | 'pricing'
  | 'onlineBooking'
  | 'healthRecord'
  | 'clinicCampaign'
  | 'servicePackages'
  | 'leadGen'

export type SpecialtyKey =
  | 'cardiology'
  | 'dermatology'
  | 'dentistry'
  | 'internal'
  | 'pediatrics'
  | 'orthopedics'
  | 'gynecology'
  | 'psychiatry'
  | 'nutrition'
  | 'other'

export type OnboardingAnswers = {
  businessType: BusinessType
  specialty: SpecialtyKey
  brandName: LocalizedText
  /** Owner personal name (account profile). */
  firstName: string
  lastName: string
  /** Iranian national ID (کد ملی), 10 digits. */
  nationalId: string
  phone: string
  address: LocalizedText
  logoUrl: string
  modules: Record<ModuleKey, boolean>
  themePrimary: string
  themeNeutral: string
  fontFamily: string
  i18nEnabled: boolean
}

export const ALL_MODULE_KEYS: ModuleKey[] = [
  'blog',
  'gallery',
  'testimonials',
  'faq',
  'shop',
  'insurance',
  'team',
  'pricing',
  'onlineBooking',
  'healthRecord',
  'clinicCampaign',
  'servicePackages',
  'leadGen',
]

/**
 * Always-on site features (not shown in module picker).
 * Kept in ModuleKey for schema gating compatibility, but forced on.
 */
export const BASE_FEATURE_MODULES: ModuleKey[] = [
  'faq',
  'gallery',
  'testimonials',
  'insurance',
  'pricing',
]

/** Optional modules the user picks during onboarding. */
export const MODULE_SELECT_ORDER: ModuleKey[] = [
  'onlineBooking',
  'healthRecord',
  'shop',
  'blog',
  'clinicCampaign',
  'leadGen',
]

export const MODULE_META: Record<
  ModuleKey,
  { label: string; description: string; icon: string }
> = {
  onlineBooking: {
    label: 'نوبت‌دهی',
    description: 'صفحه رزرو نوبت و بلاک فرم نوبت‌گیری',
    icon: 'i-lucide-calendar-days',
  },
  healthRecord: {
    label: 'سوابق سلامت',
    description: 'سابقه پایه، شرح حال و پورتال بیمار',
    icon: 'i-lucide-folder-heart',
  },
  shop: {
    label: 'محصولات',
    description: 'صفحه فروشگاه و نمایش محصولات',
    icon: 'i-lucide-shopping-bag',
  },
  pricing: {
    label: 'تعرفه‌ها',
    description: 'صفحه تعرفه‌ها و پلن‌های قیمت',
    icon: 'i-lucide-tags',
  },
  blog: {
    label: 'بلاگ',
    description: 'صفحه مقالات و آموزش بیماران',
    icon: 'i-lucide-newspaper',
  },
  team: {
    label: 'تیم پزشکی',
    description: 'صفحه و کارت‌های معرفی پزشکان',
    icon: 'i-lucide-users',
  },
  faq: {
    label: 'سؤالات متداول',
    description: 'بلاک FAQ در صفحات سایت',
    icon: 'i-lucide-circle-help',
  },
  gallery: {
    label: 'گالری',
    description: 'گالری تصاویر مطب یا کلینیک',
    icon: 'i-lucide-images',
  },
  testimonials: {
    label: 'نظرات بیماران',
    description: 'نمایش بازخورد و نظرات مراجعان',
    icon: 'i-lucide-message-circle-heart',
  },
  insurance: {
    label: 'بیمه‌ها',
    description: 'لیست بیمه‌های طرف قرارداد',
    icon: 'i-lucide-shield-check',
  },
  clinicCampaign: {
    label: 'کمپین فروش',
    description: 'کوپن، پیامک هدفمند، گردونه شانس و پیشنهادهای فروش',
    icon: 'i-lucide-megaphone',
  },
  servicePackages: {
    label: 'پکیج جلسات',
    description: 'فروش پکیج چندجلسه‌ای (فقط پلن کلینیک)',
    icon: 'i-lucide-layers',
  },
  leadGen: {
    label: 'جمع‌آوری مشتری',
    description: 'محتوای قفل‌شده، ماشین‌حساب، کوئیز و صندوق لید',
    icon: 'i-lucide-magnet',
  },
}

/** Optional module defaults by business type (base features + team are forced separately). */
export const SOLO_DEFAULT_MODULES: ModuleKey[] = ['onlineBooking', 'blog']

export const CLINIC_DEFAULT_MODULES: ModuleKey[] = ['onlineBooking']

export function withBaseModules(
  modules: Record<ModuleKey, boolean>,
): Record<ModuleKey, boolean> {
  const next = { ...modules }
  for (const key of BASE_FEATURE_MODULES) next[key] = true
  return next
}

/** Force base features on; team only for clinic. */
export function normalizeModules(
  modules: Record<ModuleKey, boolean>,
  businessType: BusinessType,
): Record<ModuleKey, boolean> {
  const next = withBaseModules(modules)
  next.team = businessType === 'clinic'
  return next
}

export function defaultModulesFor(type: BusinessType): Record<ModuleKey, boolean> {
  const on = new Set(type === 'solo' ? SOLO_DEFAULT_MODULES : CLINIC_DEFAULT_MODULES)
  for (const key of BASE_FEATURE_MODULES) on.add(key)
  if (type === 'clinic') on.add('team')
  return Object.fromEntries(ALL_MODULE_KEYS.map((k) => [k, on.has(k)])) as Record<ModuleKey, boolean>
}

export function createDefaultAnswers(): OnboardingAnswers {
  return {
    businessType: 'solo',
    specialty: 'internal',
    brandName: { fa: '', en: '' },
    firstName: '',
    lastName: '',
    nationalId: '',
    phone: '',
    address: { fa: '', en: '' },
    logoUrl: '',
    modules: defaultModulesFor('solo'),
    themePrimary: 'blue',
    themeNeutral: 'slate',
    fontFamily: 'IranYekan',
    i18nEnabled: true,
  }
}
